import * as Dialog from "@radix-ui/react-dialog";
import { modalTokens, modalSizes, typographyTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import { FiX } from "react-icons/fi";
import { Flex, Theme } from "@radix-ui/themes";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentClassName?: string;
  hideCloseButton?: boolean;
  title?: string;
  titleClassName?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  containerClassName?: string;
  size?: keyof typeof modalSizes;
  mobileFullScreen?: boolean;
};

export function Modal({
  open,
  onOpenChange,
  title,
  contentClassName,
  hideCloseButton,
  titleClassName,
  children,
  footer,
  containerClassName,
  size = "md",
  mobileFullScreen,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={modalTokens.overlay}
          onClick={() => onOpenChange(false)}
        />
        <Dialog.Content
          className={cn(modalTokens.wrapper, mobileFullScreen && "max-sm:p-0")}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Flex
            direction="column"
            className={cn(
              modalTokens.container,
              modalSizes[size],
              "max-h-[85dvh] overflow-y-auto",
              mobileFullScreen &&
                "max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:mx-0 max-sm:rounded-none max-sm:shadow-none max-sm:pt-[env(safe-area-inset-top)] max-sm:pb-[env(safe-area-inset-bottom)] max-sm:[-webkit-overflow-scrolling:touch]",
              containerClassName,
            )}
          >
            {/* Close button */}
            {!hideCloseButton && (
              <Dialog.Close
                className={cn(
                  modalTokens.closeButton,
                  mobileFullScreen &&
                    "max-sm:top-[max(0.75rem,env(safe-area-inset-top))]",
                )}
                aria-label="Close"
              >
                <FiX className="w-5 h-5" />
              </Dialog.Close>
            )}

            {/* Header */}
            {title ? (
              <Flex className="px-6 pr-14 pt-5 pb-2">
                <Dialog.Title
                  className={titleClassName || typographyTokens.modalTitle}
                >
                  {title}
                </Dialog.Title>
              </Flex>
            ) : (
              <VisuallyHidden>
                <Dialog.Title>Modal</Dialog.Title>
              </VisuallyHidden>
            )}

            {/* Body */}
            <Theme>
              <Flex
                direction="column"
                className={cn(
                  "px-6 pt-6 pb-4",
                  mobileFullScreen && "max-sm:px-5 max-sm:pt-4 max-sm:pb-20",
                  contentClassName,
                )}
              >
                {children}
              </Flex>
            </Theme>

            {/* Footer */}
            {footer && <Flex className={modalTokens.footer}>{footer}</Flex>}
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default Modal;
